import { useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import Header from "./components/Header";
import Menu from "./components/Menu";
import Login from "./views/User/Login";
import Logout from "./views/User/LogOut";
import Dashboard from "./views/Dashboard/Dashboard";
import Link from "./views/User/Link";
import Job from "./views/Job/Job";
import Payment from "./views/Payment/Payment";
import MessageCenter from "./views/MessageCenter/MessageCenter";
import NotFound from "./views/Error";
import useMessages from "./hooks/useMessages";
import useUserAccount from "./hooks/useUserAccount";
import SignIn from "./views/User/SignIn";
import useEnv from "./hooks/useEnv";
import Settings from "./views/User/Settings";
import Landing from "./views/Landing";
import useReloader from "./hooks/useReloader";
import { defineMessages, injectIntl } from "react-intl";
import PropTypes from "prop-types";

const messages = defineMessages({
    newVersionAvailable: {
        id: "Uusi versio saatavilla, paina OK ladataksesi sivu uudelleen.",
        defaultMessage: "Uusi versio saatavilla, paina OK ladataksesi sivu uudelleen."
    }
})

const App = (props) => {
    const lng = props.intl.formatMessage;
    const location = useLocation();
    const [maintenance, setMaintenance] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    const { initReloadListeners } = useReloader(lng(messages.newVersionAvailable));
    const { isEnvTruthy, isDev, getEnv } = useEnv();
    const isGlobal = getEnv('LOCALE') === 'global';
    const { fetchMessages, checkMessageCenterMaintenance } = useMessages();
    const { userIsAuthenticated } = useUserAccount();
    const menuToggle = (show) => {
        if (show === false || show === true) {
            setShowMenu(show);
        } else {
            setShowMenu((currentState) => !currentState);
        }
    }

    useEffect(() => {
        // Initialize reloader to check for new versions
        initReloadListeners();
    }, []);

    useEffect(() => {
        const messageCenterTrigger = async () => {
            try {
                const maintenance = await checkMessageCenterMaintenance();
                setMaintenance(maintenance);
                if (!maintenance) {
                    await fetchMessages();
                }
            } catch (e) {
                // fetchMessages can be slow since the backend fetches messages via a Jira integration,
                // so the request is sometimes aborted (e.g. by the browser on navigation) - not an actual error
                if (e.code === 'ECONNABORTED') {
                    if (isDev) {
                        console.log('Message center request aborted:', e);
                    }
                } else {
                    Sentry.captureException(e);
                }
            }
        };
        if (isEnvTruthy('MESSAGECENTER') && userIsAuthenticated()) {
            void messageCenterTrigger();
        }
    }, [userIsAuthenticated()]);

    /**
     * Check if header should be shown based on the current location.
     * Should be false only for login and logout pages.
     * @param {object} location
     * @returns {boolean}
     */
    const shouldShowHeader = (location) => {
        const pathname = location.pathname;
        const noMenuPaths = [
            '/', '/login', '/logout', '/user/logout', '/user/signin',
            '/user/confirmsignin', '/user/confirmsignup', '/payment'
        ];
        return !noMenuPaths.includes(pathname) && !/^\/link\/.+$/.test(pathname);
    }

    /**
     * Check if authentication is in progress based on the current location.
     * @param location
     * @returns {boolean}
     */
    const authIsInProgress = (location) => {
        // Check if we are in job search and uuid is present in the URL
        // This will trigger the authentication process and we must wait for it to finish
        return /.?\/jobsearch\/.+|.?\/job\/.+/.test(location.pathname);
    }

    return (
        <div className="site-container">
            <div id="loading">
                <div className="loader-container">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path opacity=".4" d="M256 64C150 64 64 150 64 256s86 192 192 192c70.1 0 131.3-37.5 164.9-93.6l.1 .1c-6.9 14.9-1.5 32.8 13 41.2c15.3 8.9 34.9 3.6 43.7-11.7c.2-.3 .4-.6 .5-.9l0 0C434.1 460.1 351.1 512 256 512C114.6 512 0 397.4 0 256S114.6 0 256 0c-17.7 0-32 14.3-32 32s14.3 32 32 32z" /><path d="M224 32c0-17.7 14.3-32 32-32C397.4 0 512 114.6 512 256c0 46.6-12.5 90.4-34.3 128c-8.8 15.3-28.4 20.5-43.7 11.7s-20.5-28.4-11.7-43.7c16.3-28.2 25.7-61 25.7-96c0-106-86-192-192-192c-17.7 0-32-14.3-32-32z" /></svg>
                    <div className="content" />
                </div>
            </div>
            {shouldShowHeader(location) && (
                <Header
                    menuToggle={menuToggle}
                    authInProgress={authIsInProgress(location)}
                />
            )}
            <div id='main-container' className={'p-0 d-flex h-100 position-relative ' + (location.pathname === '/' ? " login-container" : "")}>
                {shouldShowHeader(location) && (
                    <Menu
                        show={showMenu}
                        menuToggle={menuToggle}
                    />
                )}
                <Routes>
                    <Route path="/" element={<Landing />} />
                    {isGlobal ? (
                        <Route path="/*" element={<Navigate to="/" replace />} />
                    ) : (<>
                        <Route path="/login" element={<Login />} />
                        <Route path="/logout" element={<Logout />} />

                        {/* Sign up, sign in & user account actions */}
                        <Route path="/user/signin" element={isEnvTruthy('USER_ID_SIGN_IN') ? <SignIn /> : <NotFound />} />
                        <Route path="/user/confirmsignin" element={isEnvTruthy('USER_ID_SIGN_IN') ? <SignIn confirmAction={"confirmSignIn"} /> : <NotFound />} />
                        <Route path="/user/confirmsignup" element={isEnvTruthy('USER_ID_SIGN_IN') ? <SignIn confirmAction={"confirmSignUp"} /> : <NotFound />} />
                        <Route path="/user/settings/accountdetails" element={isEnvTruthy('USER_ID_SIGN_IN') ? <Settings section={'accountDetails'} /> : <NotFound />} />
                        <Route path="/user/settings/bankaccount" element={isEnvTruthy('USER_ID_SIGN_IN') ? <Settings section={'bankAccount'} /> : <NotFound />} />
                        <Route path="/user/settings/passkeys" element={isEnvTruthy('USER_ID_SIGN_IN') ? <Settings section={'passkeys'} /> : <NotFound />} />
                        <Route path="/user/settings" element={isEnvTruthy('USER_ID_SIGN_IN') ? <Settings /> : <NotFound />} />
                        <Route path="/user/logout" element={<Logout />} />

                        {/* Dashboard & invoice page */}
                        <Route path="/jobsearch/:uuid" element={<Dashboard />} />
                        <Route path="/jobsearch/" element={<Dashboard />} />
                        <Route path="/link/:dpid/:customertype?" element={<Link />} />
                        <Route path="/job/:uuid/:paymentmethod?" element={<Job maintenance={maintenance} />} />
                        <Route path="/job/:uuid" element={<Job maintenance={maintenance} />} />
                        <Route path="/job" element={<Job maintenance={maintenance} />} />

                        {/* Payment actions */}
                        <Route path="/payment/:provider/:status" element={<Payment />} />
                        <Route path="/payment" element={<Payment />} />

                        {/* Message Center */}
                        <Route path="/messagecenter" element={<MessageCenter maintenance={maintenance} />} />

                        {/* Not Found page */}
                        <Route path="/*" element={<NotFound />} />
                    </>)}
                </Routes>
            </div>
        </div>
    );
};

App.propTypes = {
    'intl': PropTypes.object.isRequired
};

export default injectIntl(App);
